import {Marked, marked} from 'marked'
import {INLINE_ALLOWED_TAGS, sanitize, type SafeHTMLString} from '@github-ui/sanitize'

// This config reflects that allowed by our server side filters, see https://github.com/github/github/blob/28257fc212b2f36422f7a151b4bac3b7828f0f06/lib/html/pipeline/sanitization_filter.rb#L40-L46
const DompurifyConfig = {
  FORBID_TAGS: ['style'], // Style should always be forbidden, otherwise it can lead to UI Redressing
  // Forbid class, id, and style attributes to prevent DOM XSS attacks where injected
  // class names (e.g., js-comment-edit-button) trigger GitHub's JS behavior hooks,
  // injected id attributes allow URL fragment targeting (e.g., #clickme), and
  // inline styles enable UI redressing/phishing (e.g., position:fixed overlays).
  // The server-side SanitizationFilter does not allow class, id, or style on any element.
  // See: https://github.com/github/issues/issues/19987
  FORBID_ATTR: ['class', 'id', 'style'],
  ALLOW_DATA_ATTR: false,
  // Always use ALLOWED_TAGS instead of ADD_TAGS to override the
  // default DOMPurify config, making only the tags explicitly
  // allowed by this config to be considered safe
  ALLOWED_TAGS: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'h7',
    'h8',
    'br',
    'b',
    'i',
    'strong',
    'em',
    'a',
    'pre',
    'code',
    'img',
    'tt',
    'div',
    'span',
    'ins',
    'del',
    'sup',
    'sub',
    'p',
    'ol',
    'ul',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'blockquote',
    'dl',
    'dt',
    'dd',
    'kbd',
    'q',
    'samp',
    'var',
    'hr',
    'ruby',
    'rt',
    'rp',
    'li',
    'tr',
    'td',
    'th',
    's',
    'strike',
    'summary',
    'details',
    'picture',
    'source',
  ],
}

// This function checks if the markdown can be parsed optimistically
// Task lists can not be parsed optimistically since they require styling to avoid jitter
// and this is not allowed for security reasons
export function canBeOptimisticallyParsed(markdown: string): boolean {
  // Check for the presence of certain syntax that indicates the markdown can be parsed optimistically
  // Matches lines that start with - [ ] or - [x] (optionally *, +, or -), possibly with whitespace before
  const optimisticSyntax = /^\s*[-*+] \[[ xX]\] .*/m
  return !optimisticSyntax.test(markdown)
}

// Ideally we'd use mintUnsafeStaticHTML(), but the trusted types type is not assignable to `string` which creates
// wide-reaching type errors across the app
// eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
const emptyHtml = '' as SafeHTMLString

export function md2html(markdown: string) {
  try {
    const dirtyHTML = marked.parse(markdown, {
      gfm: true,
      breaks: true,
      silent: true, // Prevents errors from being thrown for invalid markdown
    })
    /* eslint-disable-next-line @github-ui/github-monorepo/no-custom-sanitize-config --
       Existing custom sanitizer config; migrate to a named preset or VerifiedHTML when changed. */
    return sanitize(dirtyHTML, {...DompurifyConfig, RETURN_TRUSTED_TYPE: false})
  } catch {
    // If there's an error in parsing, return an empty string
    return emptyHtml
  }
}

/**
 * Like `md2html` but inline-only, for single-line contexts such as an issue title:
 * `parseInline` keeps block markdown from becoming `<h1>`/`<ul>`, the inline allowlist strips
 * literal block HTML to text, and `ALLOWED_ATTR: []` + `ALLOW_ARIA_ATTR: false` drop every
 * attribute (a decorative title needs none, and this stops a literal `tabindex`/`role`/`aria-*`
 * from adding a focus target or changing the accessible name).
 */
// Render every `<img>` (Markdown `![alt](url)` or literal HTML) as its alt text: `<img>` isn't in
// the allowlist, so DOMPurify would otherwise drop it and its alt, blanking an image-only title.
// Done at the marked token level (pure string work, no DOM) so it's identical in browser and SSR
// and never prefetches an untrusted image URL. A scoped `Marked` keeps this off the global `marked`.
function imgTagToAltText(tag: string): string {
  const match = /\balt\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i.exec(tag)
  const alt = match?.[1] ?? match?.[2] ?? match?.[3] ?? ''
  // The alt value comes from HTML source, so it's already encoded — preserve entities like `&amp;`
  // for the parser to decode (escaping `&` again would render a literal `&amp;`). Only neutralize
  // literal tag delimiters so a stray `<`/`>` in the value can't be parsed as markup.
  return alt.replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

const inlineMarked = new Marked({gfm: true, breaks: true})
inlineMarked.use({
  renderer: {
    image: (_href, _title, text) => text ?? '',
    // Match a whole `<img>` tag, quote-aware so a `>` inside a quoted attribute (e.g.
    // `alt="a > b"`) doesn't end the match early and leak the remainder into the title.
    html: (token: string) => token.replace(/<img\b(?:[^>"']|"[^"]*"|'[^']*')*>/gi, imgTagToAltText),
  },
})

export function md2htmlInline(markdown: string) {
  try {
    // `parseInline` runs synchronously here, but its return type is `string | Promise | undefined`
    // — guard so `sanitize` only sees a string.
    const dirtyHTML = inlineMarked.parseInline(markdown, {gfm: true, breaks: true, silent: true})
    if (typeof dirtyHTML !== 'string') return emptyHtml
    /* eslint-disable-next-line @github-ui/github-monorepo/no-custom-sanitize-config --
       Existing custom sanitizer config; migrate to a named preset or VerifiedHTML when changed. */
    return sanitize(dirtyHTML, {
      ...DompurifyConfig,
      ALLOWED_TAGS: [...INLINE_ALLOWED_TAGS],
      ALLOWED_ATTR: [],
      ALLOW_ARIA_ATTR: false,
      RETURN_TRUSTED_TYPE: false,
    })
  } catch {
    return emptyHtml
  }
}

const disabled = () => undefined

const titleMarked = new Marked({
  gfm: false,
  breaks: false,
  tokenizer: {
    escape: disabled,
    tag: disabled,
    link: disabled,
    reflink: disabled,
    emStrong: disabled,
    br: disabled,
    del: disabled,
    autolink: disabled,
    url: disabled,
  },
})

/**
 * Render Markdown inline title. Only backticks (inline code) are supported. Parallels
 * `lib/github/goomba/title_markdown_filter.rb`, except it doesn't currently support custom emoji shortcodes.
 */
export function md2htmlTitle(markdown: string) {
  try {
    // `parseInline` runs synchronously here, but its return type is `string | Promise | undefined`
    // — guard so `sanitize` only sees a string.
    const dirtyHTML = titleMarked.parseInline(markdown, {gfm: true, breaks: true, silent: true})
    if (typeof dirtyHTML !== 'string') return emptyHtml
    /* eslint-disable-next-line @github-ui/github-monorepo/no-custom-sanitize-config --
       Existing custom sanitizer config; migrate to a named preset or VerifiedHTML when changed.
       We should probably introduce a new config for titles which only allows `code` tags. */
    return sanitize(dirtyHTML, {
      ...DompurifyConfig,
      ALLOWED_TAGS: [...INLINE_ALLOWED_TAGS],
      ALLOWED_ATTR: [],
      ALLOW_ARIA_ATTR: false,
      RETURN_TRUSTED_TYPE: false,
    })
  } catch {
    return emptyHtml
  }
}
